/**
 * Обогащение полилинии маршрута навигационными данными: кумулятивные дистанции
 * и повороты. Адаптация GreenGlobe RouteProcessor.ts (статик-класс → функции,
 * гео-математика — из @app/geo).
 */

import { bearingDeg, bearingDiffDeg, haversineM, type LonLat } from '@app/geo'
import { TURN_ANGLE_THRESHOLD_DEG } from './config'

export interface EnrichedPoint {
  point: LonLat
  index: number
  /** Кумулятивная дистанция от старта маршрута, м. */
  distanceFromStartM: number
  /** Изменение курса в вершине (−180..180, минус — налево); null на концах. */
  turnDeg: number | null
}

/** Значимый поворот маршрута (для стрелок и подсказок UI). */
export interface RouteStep {
  point: LonLat
  index: number
  turn: 'left' | 'right'
  turnDeg: number
  distanceFromStartM: number
}

/** Кумулятивные дистанции + углы поворота для каждой вершины полилинии. */
export function enrich(polyline: readonly LonLat[]): EnrichedPoint[] {
  const out: EnrichedPoint[] = []
  let cumulativeM = 0
  let prevBearing: number | null = null
  for (let i = 0; i < polyline.length; i++) {
    const point = polyline[i]
    if (!point) continue
    const next = polyline[i + 1]
    const bearingToNext = next ? bearingDeg(point, next) : null
    const turnDeg =
      prevBearing !== null && bearingToNext !== null
        ? bearingDiffDeg(prevBearing, bearingToNext)
        : null
    out.push({ point, index: i, distanceFromStartM: cumulativeM, turnDeg })
    if (next) cumulativeM += haversineM(point, next)
    if (bearingToNext !== null) prevBearing = bearingToNext
  }
  return out
}

/** Полная длина маршрута = кумулятив последней вершины. */
export function totalDistanceM(enriched: readonly EnrichedPoint[]): number {
  return enriched[enriched.length - 1]?.distanceFromStartM ?? 0
}

/** Изломы ближе этого сливаются в один поворот (рисованная дорога — кривая). */
const MERGE_WINDOW_M = 15

/** Ниже этого угла вершина — «прямо», разрывает группу изломов. */
const MICRO_TURN_DEG = 5

/**
 * Повороты маршрута. Соседние изломы в окне MERGE_WINDOW_M группируются в один
 * шаг с СУММОЙ углов (точка — самый резкий излом группы): нарисованная от руки
 * дорога из многих вершин больше не даёт «поворот на каждом изгибе», а плавная
 * дуга с суммой ≤ порога — вообще не поворот (фидбек владельца 23.07).
 */
export function extractSteps(polyline: readonly LonLat[]): RouteStep[] {
  const steps: RouteStep[] = []
  let group: EnrichedPoint[] = []

  const flush = (): void => {
    if (group.length === 0) return
    const sumDeg = group.reduce((acc, p) => acc + (p.turnDeg ?? 0), 0)
    if (Math.abs(sumDeg) > TURN_ANGLE_THRESHOLD_DEG) {
      const sharpest = group.reduce((a, b) =>
        Math.abs(b.turnDeg ?? 0) > Math.abs(a.turnDeg ?? 0) ? b : a,
      )
      steps.push({
        point: sharpest.point,
        index: sharpest.index,
        turn: sumDeg < 0 ? 'left' : 'right',
        turnDeg: sumDeg,
        distanceFromStartM: sharpest.distanceFromStartM,
      })
    }
    group = []
  }

  for (const p of enrich(polyline)) {
    if (p.turnDeg === null || Math.abs(p.turnDeg) < MICRO_TURN_DEG) {
      // Прямой участок: если от последнего излома группы прошло больше окна —
      // группа закончилась.
      const last = group[group.length - 1]
      if (last && p.distanceFromStartM - last.distanceFromStartM > MERGE_WINDOW_M) flush()
      continue
    }
    const last = group[group.length - 1]
    if (last && p.distanceFromStartM - last.distanceFromStartM > MERGE_WINDOW_M) flush()
    group.push(p)
  }
  flush()
  return steps
}
